/**
 * MobileNavContext — drawer open/close state + current page title for the mobile top bar.
 *
 * Used only below the `lg` breakpoint. At `lg`+ the persistent sidebar renders and the
 * top bar / drawer are hidden, so this state is harmless on desktop.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

interface MobileNavValue {
  drawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
  toggleDrawer: () => void
  /** Title shown in the mobile top bar (e.g. the account name on trade pages). */
  pageTitle: string
  pageSubtitle: string
  setPageTitle: (title: string, subtitle?: string) => void
}

const MobileNavContext = createContext<MobileNavValue | null>(null)

export function MobileNavProvider({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [pageTitle, setTitleState] = useState('')
  const [pageSubtitle, setSubtitleState] = useState('')

  const openDrawer = useCallback(() => setDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])
  const toggleDrawer = useCallback(() => setDrawerOpen((v) => !v), [])
  const setPageTitle = useCallback((title: string, subtitle = '') => {
    setTitleState(title)
    setSubtitleState(subtitle)
  }, [])

  const value = useMemo<MobileNavValue>(
    () => ({ drawerOpen, openDrawer, closeDrawer, toggleDrawer, pageTitle, pageSubtitle, setPageTitle }),
    [drawerOpen, openDrawer, closeDrawer, toggleDrawer, pageTitle, pageSubtitle, setPageTitle]
  )

  return <MobileNavContext.Provider value={value}>{children}</MobileNavContext.Provider>
}

export function useMobileNav(): MobileNavValue {
  const ctx = useContext(MobileNavContext)
  if (!ctx) {
    // Safe fallback so components don't crash if used outside the provider.
    return {
      drawerOpen: false,
      openDrawer: () => {},
      closeDrawer: () => {},
      toggleDrawer: () => {},
      pageTitle: '',
      pageSubtitle: '',
      setPageTitle: () => {},
    }
  }
  return ctx
}
